import { useEffect, useRef, useState } from 'react'

import { RibbonScene } from '../ribbon/RibbonScene'
import { getScrollProgress } from '../ribbon/scrollProgress'

interface RibbonCanvasProps {
  onController: (controller: RibbonScene | null) => void
}

const readDocumentProgress = (): number =>
  getScrollProgress({
    scrollHeight: document.documentElement.scrollHeight,
    scrollY: window.scrollY,
    viewportHeight: window.innerHeight,
  })

export function RibbonCanvas({
  onController,
}: RibbonCanvasProps): React.JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const onControllerRef = useRef(onController)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    onControllerRef.current = onController
  }, [onController])

  useEffect(() => {
    const canvas = canvasRef.current

    if (!canvas) {
      return
    }

    let controller: RibbonScene

    try {
      controller = new RibbonScene(canvas)
    } catch (caughtError) {
      const message =
        caughtError instanceof Error
          ? caughtError.message
          : 'WebGL could not be initialised.'
      window.setTimeout(() => {
        setError(message)
      }, 0)
      return
    }

    const reducedMotionQuery = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    )
    const handleResize = (): void => {
      controller.resize(window.innerWidth, window.innerHeight)
    }
    const handleScroll = (): void => {
      controller.setProgress(readDocumentProgress())
    }
    const handleReducedMotion = (event: MediaQueryListEvent): void => {
      controller.setReducedMotion(event.matches)
    }

    controller.setReducedMotion(reducedMotionQuery.matches)
    handleResize()
    handleScroll()
    controller.start()
    onControllerRef.current(controller)

    window.addEventListener('resize', handleResize, { passive: true })
    window.addEventListener('scroll', handleScroll, { passive: true })
    reducedMotionQuery.addEventListener('change', handleReducedMotion)

    if (import.meta.env.DEV) {
      window.__RIBBON_DEBUG__ = {
        getDiagnostics: () => controller.getDiagnostics(),
        setProgress: (progress, immediate = false) => {
          const maximumScroll = Math.max(
            0,
            document.documentElement.scrollHeight - window.innerHeight,
          )
          window.scrollTo({
            behavior: 'instant',
            left: 0,
            top: maximumScroll * progress,
          })
          controller.setProgress(progress, immediate)
        },
        setReducedMotion: (reduced) => {
          controller.setReducedMotion(reduced)
        },
        settle: (frames) => {
          controller.settle(frames)
        },
        updateTuning: (key, value) => {
          controller.updateTuning(key, value)
        },
      }
    }

    return () => {
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('scroll', handleScroll)
      reducedMotionQuery.removeEventListener('change', handleReducedMotion)
      delete window.__RIBBON_DEBUG__
      onControllerRef.current(null)
      controller.dispose()
    }
  }, [])

  return (
    <div className="canvas-shell" aria-hidden="true">
      <canvas ref={canvasRef} className="ribbon-canvas" />
      {error ? (
        <div className="webgl-error">
          <p>WebGL is unavailable.</p>
          <small>{error}</small>
        </div>
      ) : null}
    </div>
  )
}
